import React, { useState, useEffect, useRef, Dispatch, SetStateAction, useCallback } from 'react';
import { Box, Modal, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Virtualize } from '../../../Components/CustomUIElements/VirtualizedAutocomplete';
import {
  MultilineFormTextField,
  FormTextField
} from '../../../Components/CustomUIElements/FormTextField';
import FormSelectField from '../../../Components/CustomUIElements/FormSelectField';
import { StyledSnackbarProps } from '../../../Components/CustomUIElements/StyledSnackbar';
import { classes } from '../styles';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import Api from '../../API';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableChoice from './SortableChoice';
import { useFieldChoiceList } from '../../../hooks/useFieldChoiceList';
import BooleanFieldSwitch from './BooleanFieldSwitch';
import { formatUserPermissions } from '../../../Components/Utilities/formatUserPermissions';

const fieldTypeOptions = [
  'Availability',
  'Date',
  'Text',
  'Textarea',
  'Radio Button',
  'Select',
  'Rank',
  'CSV Data Lookup',
  'Checkbox'
];

const fieldTypeValues = {
  Select: 'select_field',
  'CSV Data Lookup': 'csv_data_lookup_field',
  Date: 'date_field',
  Text: 'text_field',
  'Radio Button': 'radio_button_field',
  Rank: 'rank_field',
  'Job Reference Lookup': 'job_reference_lookup_field',
  Availability: 'availability_field',
  Checkbox: 'check_box_field',
  Textarea: 'text_area_field'
};

const DateOptions = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function NewField({
  approvalTemplateId,
  setSnackbar,
  modalsOpen,
  setModalsOpen,
  editingQuestion,
  getQuestions
}: {
  modalsOpen: Record<string, boolean>;
  setModalsOpen: Dispatch<SetStateAction<Record<string, boolean>>>;
  approvalTemplateId: number;
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
  editingQuestion?: any;
  getQuestions: any;
}) {
  const userPermissions = formatUserPermissions();
  const [isCreating, setIsCreating] = useState(false);
  const [fieldType, setFieldType] = useState('');
  const [label, setLabel] = useState('');
  const [enabledSwitch, setEnabledSwitch] = useState(true);
  const [requiredSwitch, setRequiredSwitch] = useState(false);
  const [confidentialSwitch, setConfidentialSwitch] = useState(false);
  const [rateableSwitch, setRateableSwitch] = useState(false);
  const [weekDuration, setWeekDuration] = useState('Weekly');
  const [weekStartDay, setWeekStartDay] = useState<string>('Sunday');
  const [headingOne, setHeadingOne] = useState('');
  const [headingTwo, setHeadingTwo] = useState('');
  const [allowBreak, setAllowBreak] = useState('No');
  const [errorLabel, setErrorLabel] = useState('');
  const [errorField, setErrorField] = useState('');
  const [errorChoices, setErrorChoices] = useState('');
  const {
    choiceList,
    setChoiceList,
    addChoice,
    removeChoice,
    clearChoices,
    updateChoice,
    switchChoices
  } = useFieldChoiceList();

  const labelRef = useRef<HTMLInputElement>(null);
  const fieldTypeRef = useRef<HTMLInputElement>(null);
  const choicesRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const isSystemField = editingQuestion && !editingQuestion.editable;

  function handleDragEnd(event) {
    const { active, over } = event;
    switchChoices(active.id, over.id);
  }

  const getKeyByValue = (object, value) => {
    return Object.keys(object).find((key) => object[key] === value);
  };

  const setDefaultValue = useCallback(async () => {
    if (editingQuestion) {
      setFieldType(getKeyByValue(fieldTypeValues, editingQuestion.field_type));
      setLabel(editingQuestion.title);
      setEnabledSwitch(editingQuestion.enabled);
      setRequiredSwitch(editingQuestion.required);
      setConfidentialSwitch(editingQuestion.confidential);
      setRateableSwitch(editingQuestion.rateable);
      setChoiceList(editingQuestion.field_choices);
      if (editingQuestion.availability_config) {
        const duration = editingQuestion.availability_config.week_duration;
        setWeekDuration(duration.charAt(0).toUpperCase() + duration.slice(1));
        setWeekStartDay(DateOptions[editingQuestion.availability_config.week_start_day]);
        setHeadingOne(editingQuestion.availability_config.heading_1);
        setHeadingTwo(editingQuestion.availability_config.heading_2);
        setAllowBreak(editingQuestion.availability_config.allow_break === true ? 'Yes' : 'No');
      }
    }
  }, [editingQuestion]);

  const handleClose = () => {
    setModalsOpen((prev) => ({ ...prev, newField: false }));
    clearData();
  };

  const handleSubmit = async (approvalTemplateId: number) => {
    if (!fieldType || !label) {
      if (!fieldType) {
        fieldTypeRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setErrorField('This field is required');
      } else {
        labelRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setErrorLabel('This field is required');
      }
      return;
    }
    setIsCreating(true);

    const fieldObject = {
      title: label,
      enabled: enabledSwitch,
      required: requiredSwitch,
      confidential: confidentialSwitch,
      rateable: rateableSwitch,
      field_type: fieldTypeValues[fieldType]
    };

    if (['Select', 'Rank', 'Checkbox', 'Radio Button'].includes(fieldType)) {
      choiceList.map((choice, index) => {
        if (!choice?.name) {
          choicesRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setErrorChoices('All field choices must each have a value');
          return;
        }
        choice.position = index;
        if (choice.id.toString().length == 13) {
          choice.id = '';
        }
      });

      const jsonString = JSON.stringify(Object.assign({}, choiceList));
      const attributes = JSON.parse(jsonString);
      fieldObject['field_choices_attributes'] = attributes;
    } else if (['Availability'].includes(fieldType)) {
      fieldObject['availability_config_attributes'] = {
        week_duration: weekDuration.toLowerCase(),
        week_start_day: DateOptions.indexOf(weekStartDay),
        heading_1: headingOne,
        heading_2: headingTwo,
        allow_break: allowBreak === 'Yes'
      };
    }

    try {
      if (editingQuestion) {
        const data = await Api.putQuestion(editingQuestion.id, approvalTemplateId, {
          field: fieldObject
        });
        setSnackbar({
          message: 'Field has been updated',
          state: 'success'
        });
      } else {
        const data = await Api.newField(approvalTemplateId, {
          field: fieldObject
        });
        setSnackbar({
          message: 'Field has been created',
          state: 'success'
        });
        clearData();
      }
      handleClose();
    } catch (error: unknown) {
      setSnackbar({
        message: `Field failed to create: ${
          (
            error as {
              errors: string;
            }
          )?.errors || 'Unknown error'
        }`,
        state: 'error'
      });
    } finally {
      setIsCreating(false);
      getQuestions();
    }
  };

  const handleChoiceInput = (e, index: number) => {
    const { value } = e.target;
    updateChoice(value, index);
  };

  const clearData = () => {
    setFieldType('');
    setLabel('');
    setEnabledSwitch(true);
    setRequiredSwitch(false);
    setConfidentialSwitch(false);
    setRateableSwitch(false);
    clearChoices();
    setWeekDuration('Weekly');
    setWeekStartDay('Sunday');
    setHeadingOne('');
    setHeadingTwo('');
    setAllowBreak('No');
    setErrorLabel('');
    setErrorField('');
    setErrorChoices('');
  };

  useEffect(() => {
    setDefaultValue();
  }, [setDefaultValue, editingQuestion]);

  return (
    <Modal
      open={modalsOpen.newField}
      onClose={() => handleClose(true)}
      aria-labelledby="new-field-modal"
    >
      <Box sx={classes.actionsModalReducedPadding}>
        <Box sx={classes.modalWrapper}>
          <CloseIcon onClick={() => handleClose(true)} sx={classes.closeIcon} />
          <Box id="new-field-title" sx={classes.actionModalHeader}>
            New field
          </Box>
          <Box
            sx={classes.modalContent}
            id="new-field-modal-content"
            aria-label="New Field Modal Content"
          >
            <Box role="form" id="new-field-form" aria-label="New Field form">
              {!isSystemField && (
                <Box sx={{ ...classes.actionModalFormLine, marginTop: '20px' }} ref={fieldTypeRef}>
                  <Virtualize
                    value={fieldType}
                    passOptions={
                      userPermissions?.['Approval Forms']?.['Use Availability Questions']
                        ? fieldTypeOptions
                        : fieldTypeOptions.slice(1)
                    }
                    setValue={(value: string) => {
                      setFieldType(value);
                      setErrorField('');
                    }}
                    passedStyles={{
                      ...sharedClasses.formAutocomplete,
                      width: '350px',
                      marginLeft: '3px'
                    }}
                    label="Field type"
                  />
                  {errorField && <Box sx={{ ...classes.formError, top: '63px' }}>{errorField}</Box>}
                </Box>
              )}
            </Box>
            <Box sx={{ marginLeft: '15px' }}>
              <Box sx={classes.modalFormLine}>
                <MultilineFormTextField
                  value={label}
                  onChange={(e) => {
                    setErrorLabel('');
                    setLabel(e.target.value);
                  }}
                  fullWidth
                  rows={6}
                  innerRef={labelRef}
                  error={errorLabel}
                  label={'Field label'}
                />
              </Box>
            </Box>
            <Box sx={classes.checkboxContainer} id="enabled-filter">
              <BooleanFieldSwitch
                fieldValue={enabledSwitch}
                setFieldValue={setEnabledSwitch}
                label="Enabled"
                isSystemField={false}
              />
              <BooleanFieldSwitch
                fieldValue={requiredSwitch}
                setFieldValue={setRequiredSwitch}
                label="Required"
                isSystemField={isSystemField}
              />
              <BooleanFieldSwitch
                fieldValue={confidentialSwitch}
                setFieldValue={setConfidentialSwitch}
                label="Confidential"
                isSystemField={isSystemField}
              />
            </Box>
            {['Select', 'Rank', 'Checkbox', 'Radio Button'].includes(fieldType) && (
              <Box sx={{ ...classes.addChoiceContainer, paddingRight: 'unset' }} ref={choicesRef}>
                <Box sx={classes.addChoiceHead}>
                  <Box id="add-choice-title" sx={classes.addChoiceLabel}>
                    Choices
                  </Box>
                  <Button
                    id="add-choice-button"
                    sx={classes.modalAddChoiceButton}
                    onClick={addChoice}
                  >
                    Add choice
                  </Button>
                </Box>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={choiceList} strategy={verticalListSortingStrategy}>
                    {choiceList.map(
                      (choice, index) =>
                        !(choice?._destroy === 1) && (
                          <SortableChoice
                            key={choice.id}
                            id={choice.id}
                            choice={choice}
                            index={index}
                            handleChoiceInput={handleChoiceInput}
                            onRemoveChoice={removeChoice}
                          />
                        )
                    )}
                  </SortableContext>
                </DndContext>
                {errorChoices && <Box sx={{ ...classes.inlineError }}>{errorChoices}</Box>}
              </Box>
            )}
            {['Availability'].includes(fieldType) && (
              <Box sx={classes.availabilityContainer}>
                <Box id="availability-config-title" sx={classes.addChoiceLabel}>
                  Availability Config
                </Box>
                <Box sx={classes.config}>
                  <FormSelectField
                    options={['Weekly', 'Fortnightly']}
                    defaultValue={weekDuration}
                    value={weekDuration}
                    onChange={(value) => setWeekDuration(value)}
                    label={'How long is your work period?'}
                    styles={{ width: '290px', marginBottom: '15px' }}
                    required={true}
                  />
                  <FormSelectField
                    options={DateOptions}
                    defaultValue={weekStartDay}
                    value={weekStartDay}
                    onChange={(value) => {
                      setWeekStartDay(value);
                    }}
                    label={'When does the work period start?'}
                    styles={{ width: '290px', marginBottom: '15px' }}
                  />
                  <FormTextField
                    label="Heading 1"
                    value={headingOne}
                    onChange={(e) => setHeadingOne(e.target.value)}
                    required={false}
                    styles={{ width: '290px', marginBottom: '15px' }}
                  />
                  <FormTextField
                    label="Heading 2"
                    value={headingTwo}
                    onChange={(e) => setHeadingTwo(e.target.value)}
                    required={false}
                    styles={{ width: '290px', marginBottom: '15px' }}
                  />
                  <FormSelectField
                    options={['Yes', 'No']}
                    defaultValue={allowBreak}
                    value={allowBreak}
                    onChange={(value) => setAllowBreak(value)}
                    label={'Allow break?'}
                    styles={{ width: '290px', marginBottom: '15px' }}
                  />
                </Box>
              </Box>
            )}
            <Box sx={classes.modalFooter}>
              <Button
                id="close-new-field-button"
                sx={classes.modalCancelButton}
                onClick={() => handleClose(true)}
              >
                Cancel
              </Button>
              <Button
                id="submit-new-field-button"
                type="submit"
                sx={{ ...classes.modalCreateButton, width: '91px', height: '41px' }}
                onClick={() => handleSubmit(approvalTemplateId)}
              >
                {isCreating ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
